import { useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { TOURNAMENT_LIST_PARAM } from '@/utils/tournamentListParams';

/**
 * The search text in the URL (`q`). Writing it trims the value (empty removes
 * `q`), returns the list to the first page (drops `page`) and replaces the
 * history entry. Header search writes `q` only through it.
 * The setter is stable (safe in effect dependencies).
 */
export function useSearchQueryParam(): readonly [string, (value: string) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get(TOURNAMENT_LIST_PARAM.QUERY) ?? '';
  // react-router's setter changes identity with every URL change.
  const setSearchParamsRef = useRef(setSearchParams);
  setSearchParamsRef.current = setSearchParams;

  const setQ = useCallback((value: string) => {
    setSearchParamsRef.current((prev) => {
      const next = new URLSearchParams(prev);
      const trimmed = value.trim();
      if (trimmed) next.set(TOURNAMENT_LIST_PARAM.QUERY, trimmed);
      else next.delete(TOURNAMENT_LIST_PARAM.QUERY);
      next.delete(TOURNAMENT_LIST_PARAM.PAGE);
      return next;
    }, { replace: true });
  }, []);

  return [q, setQ] as const;
}
